/**
 * CostingGrid — the editable year × line-item cost grid (DEV — costing).
 *
 * Built on the design-system Table (no grid lib). Collapsible bold category
 * header rows show recursive per-year subtotals; nested child categories + line
 * items sit beneath. Inline click-to-edit unit cost (PATCH on blur). Per-year
 * cell editing binds quantity ↔ total reciprocally (total = quantity ×
 * effective unit cost); lump-sum lines lock quantity=1 and are edited via unit
 * cost. Remove-category trash confirms via a warning modal prefilled from the
 * server dry-run counts. A line item moves category via a dropdown (rows aren't
 * draggable).
 */
import { useMemo, useState } from 'react'
import { ChevronRight, Trash2 } from 'lucide-react'

import { Modal } from '../ui'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/Table'
import {
  APPROPRIATION_LABELS,
  costingApi,
  type CostCategoryNode,
  type CostLineItemRow,
  type CostModelGrid,
} from '../../services/costing'

interface CostingGridProps {
  grid: CostModelGrid
  onChange: () => void
  /** Full category set for the move-to dropdown, so a line can still be moved
   *  across colors of money even when the grid is filtered to one tab. Defaults
   *  to the (possibly filtered) grid's own categories. */
  moveTargets?: CostCategoryNode[]
}

function fmtMoney(value: string | number): string {
  const n = typeof value === 'number' ? value : Number(value)
  if (Number.isNaN(n)) return '—'
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const SOURCE_LABELS: Record<string, string> = {
  comps: 'Comps', research: 'Research', blind: 'Estimate',
  template: 'Template', user: 'Edited',
}

function flattenCategories(cats: CostCategoryNode[], depth = 0): { cat: CostCategoryNode; depth: number }[] {
  const out: { cat: CostCategoryNode; depth: number }[] = []
  for (const cat of cats) {
    out.push({ cat, depth })
    out.push(...flattenCategories(cat.children || [], depth + 1))
  }
  return out
}

export default function CostingGrid({ grid, onChange, moveTargets }: CostingGridProps) {
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set())
  const [editingUnit, setEditingUnit] = useState<number | null>(null)
  const [unitDraft, setUnitDraft] = useState('')
  const [editingCell, setEditingCell] = useState<{ item: number; year: number } | null>(null)
  const [qtyDraft, setQtyDraft] = useState('')
  const [removeTarget, setRemoveTarget] = useState<{ cat: CostCategoryNode; counts: { sub_categories: number; line_items: number } } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const years = grid.years
  const allCategories = useMemo(
    () => flattenCategories(moveTargets ?? grid.categories),
    [moveTargets, grid.categories],
  )

  const toggle = (id: number) => {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // ── unit-cost inline edit ─────────────────────────────────────────────────
  const startUnitEdit = (item: CostLineItemRow) => {
    setEditingUnit(item.id)
    setUnitDraft(item.unit_cost)
  }
  const commitUnitEdit = async (item: CostLineItemRow) => {
    const val = unitDraft.trim()
    setEditingUnit(null)
    if (val === '' || val === item.unit_cost) return
    setBusy(true)
    setError(null)
    try {
      await costingApi.patchLineItem(item.id, { unit_cost: val })
      onChange()
    } catch (e) {
      // Surface the server's reason and reload so the cell reverts to its
      // last-saved value rather than keeping the rejected input.
      setError((e as Error).message)
      onChange()
    } finally {
      setBusy(false)
    }
  }

  // ── per-year cell edit (quantity ↔ total) ─────────────────────────────────
  const startCellEdit = (item: CostLineItemRow, cell: { year: number; quantity: string }) => {
    if (item.unit_of_measure.is_lump_sum) return // locked; edit unit cost instead
    setEditingCell({ item: item.id, year: cell.year })
    setQtyDraft(cell.quantity)
  }
  const commitCellEdit = async (item: CostLineItemRow, cell: { id: number | null; year: number }) => {
    const val = qtyDraft.trim()
    setEditingCell(null)
    if (val === '') return
    setBusy(true)
    setError(null)
    try {
      if (cell.id != null) await costingApi.patchYear(cell.id, val)
      else await costingApi.createYear(item.id, cell.year, val)
      onChange()
    } catch (e) {
      // Surface the server's reason (e.g. invalid/negative quantity) and reload
      // so the cell reverts to its last-saved value.
      setError((e as Error).message)
      onChange()
    } finally {
      setBusy(false)
    }
  }
  const totalToQuantity = (total: string, effectiveUnitCost: string): string => {
    const euc = Number(effectiveUnitCost)
    if (!euc) return qtyDraft
    return (Number(total) / euc).toFixed(4)
  }

  // ── move line item ────────────────────────────────────────────────────────
  const moveItem = async (item: CostLineItemRow, categoryId: number) => {
    if (categoryId === item.category) return
    setBusy(true)
    setError(null)
    try {
      await costingApi.patchLineItem(item.id, { category: categoryId })
      onChange()
    } catch (e) {
      setError((e as Error).message)
      onChange()
    } finally {
      setBusy(false)
    }
  }

  // ── remove category subtree ───────────────────────────────────────────────
  const openRemove = async (cat: CostCategoryNode) => {
    const counts = await costingApi.deactivateCategory(cat.id, { dryRun: true })
    setRemoveTarget({ cat, counts })
  }
  const confirmRemove = async () => {
    if (!removeTarget) return
    setBusy(true)
    try {
      await costingApi.deactivateCategory(removeTarget.cat.id)
      setRemoveTarget(null)
      onChange()
    } finally {
      setBusy(false)
    }
  }

  // ── row rendering ─────────────────────────────────────────────────────────
  const renderCategory = (cat: CostCategoryNode, depth: number): JSX.Element[] => {
    const rows: JSX.Element[] = []
    const isCollapsed = collapsed.has(cat.id)
    rows.push(
      <TableRow key={`cat-${cat.id}`} className="bg-muted/40">
        <TableCell className="font-semibold" style={{ paddingLeft: 12 + depth * 20 }}>
          <div className="flex items-center gap-2">
            <button onClick={() => toggle(cat.id)} className="text-muted-foreground hover:text-foreground">
              <ChevronRight className={`w-4 h-4 transition-transform ${isCollapsed ? '' : 'rotate-90'}`} />
            </button>
            <span>{cat.name}</span>
            {depth === 0 && cat.appropriation && (
              <span className="text-[10px] uppercase tracking-wide rounded bg-muted px-1.5 py-0.5 text-muted-foreground">
                {APPROPRIATION_LABELS[cat.appropriation] ?? cat.appropriation}
              </span>
            )}
            {cat.psc_code && <span className="text-xs text-muted-foreground">{cat.psc_code}</span>}
            <button
              onClick={() => openRemove(cat)}
              className="ml-1 text-muted-foreground hover:text-destructive"
              title="Remove category"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </TableCell>
        <TableCell />
        {years.map((y) => (
          <TableCell key={y} className="text-right font-semibold tabular-nums">
            {fmtMoney(cat.year_subtotals[String(y)] ?? '0')}
          </TableCell>
        ))}
      </TableRow>,
    )
    if (isCollapsed) return rows

    for (const item of cat.line_items) {
      rows.push(renderLineItem(item, depth + 1))
    }
    for (const child of cat.children || []) {
      rows.push(...renderCategory(child, depth + 1))
    }
    return rows
  }

  const renderLineItem = (item: CostLineItemRow, depth: number): JSX.Element => {
    const isLump = item.unit_of_measure.is_lump_sum
    return (
      <TableRow key={`li-${item.id}`}>
        <TableCell style={{ paddingLeft: 12 + depth * 20 }}>
          <div className="flex items-center gap-2">
            <span>{item.name}</span>
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              {item.unit_of_measure.code} · {SOURCE_LABELS[item.estimate_source] ?? item.estimate_source}
            </span>
            <select
              value={item.category}
              onChange={(e) => moveItem(item, Number(e.target.value))}
              className="text-xs bg-transparent border border-border rounded px-1 py-0.5 text-muted-foreground"
              title="Move to category"
            >
              {allCategories.map(({ cat, depth: d }) => (
                <option key={cat.id} value={cat.id}>{' '.repeat(d * 2)}{cat.name}</option>
              ))}
            </select>
          </div>
        </TableCell>
        <TableCell className="text-right tabular-nums">
          {editingUnit === item.id ? (
            <input
              autoFocus
              className="w-24 text-right border border-primary rounded px-1 py-0.5 bg-background"
              value={unitDraft}
              onChange={(e) => setUnitDraft(e.target.value)}
              onBlur={() => commitUnitEdit(item)}
              onKeyDown={(e) => { if (e.key === 'Enter') commitUnitEdit(item) }}
            />
          ) : (
            <button className="hover:underline" onClick={() => startUnitEdit(item)}>
              {fmtMoney(item.unit_cost)}
            </button>
          )}
        </TableCell>
        {years.map((y) => {
          const cell = item.years.find((c) => c.year === y)
          const editing = editingCell?.item === item.id && editingCell?.year === y
          return (
            <TableCell key={y} className="text-right tabular-nums">
              {editing ? (
                // Two ways to edit a cell: type a quantity, or type a dollar
                // total (which back-computes the quantity). Both write the same
                // qtyDraft. Commit happens once, when focus leaves the whole
                // editor — moving between the two inputs must NOT commit, so we
                // gate on relatedTarget staying inside the container. Enter
                // blurs (→ commit); the fields are reachable in either order.
                <div
                  className="flex flex-col items-end gap-1"
                  onBlur={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                      commitCellEdit(item, { id: cell?.id ?? null, year: y })
                    }
                  }}
                >
                  <input
                    autoFocus
                    className="w-20 text-right border border-primary rounded px-1 py-0.5 bg-background"
                    value={qtyDraft}
                    onChange={(e) => setQtyDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
                  />
                  <input
                    className="w-24 text-right border border-border rounded px-1 py-0.5 bg-background text-xs"
                    placeholder="or total"
                    onChange={(e) => setQtyDraft(totalToQuantity(e.target.value, cell?.effective_unit_cost ?? '0'))}
                    onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
                  />
                </div>
              ) : (
                <button
                  className={isLump ? 'text-muted-foreground' : 'hover:underline'}
                  onClick={() => cell && startCellEdit(item, cell)}
                  disabled={isLump}
                  title={isLump ? 'Lump-sum — edit unit cost' : `Qty ${cell?.quantity ?? '0'}`}
                >
                  {cell && Number(cell.total) !== 0 ? fmtMoney(cell.total) : '—'}
                </button>
              )}
            </TableCell>
          )
        })}
      </TableRow>
    )
  }

  return (
    <div className={busy ? 'opacity-70 pointer-events-none' : ''}>
      {error && (
        <div className="mb-3 flex items-start justify-between gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          <span>{error}</span>
          <button className="shrink-0 underline" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Line Item</TableHead>
            <TableHead className="text-right">Unit Cost</TableHead>
            {years.map((y) => (
              <TableHead key={y} className="text-right">{y}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {grid.categories.flatMap((cat) => renderCategory(cat, 0))}
          <TableRow className="border-t-2 border-foreground/20">
            <TableCell className="font-bold">Grand Total</TableCell>
            <TableCell />
            {years.map((y) => (
              <TableCell key={y} className="text-right font-bold tabular-nums">
                {fmtMoney(grid.grand_totals[String(y)] ?? '0')}
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>

      <Modal
        isOpen={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        title="Remove category?"
      >
        {removeTarget && (
          <div className="space-y-4">
            <p className="text-sm text-foreground">
              This removes <strong>{removeTarget.cat.name}</strong>,{' '}
              {removeTarget.counts.sub_categories} sub-categor
              {removeTarget.counts.sub_categories === 1 ? 'y' : 'ies'}, and{' '}
              {removeTarget.counts.line_items} line item
              {removeTarget.counts.line_items === 1 ? '' : 's'}. This can't be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="px-3 py-1.5 text-sm rounded border border-border hover:bg-muted"
                onClick={() => setRemoveTarget(null)}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1.5 text-sm rounded bg-destructive text-destructive-foreground hover:opacity-90"
                onClick={confirmRemove}
              >
                Remove
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
